import { useEffect, type RefObject } from "react";

import type { FeedbackCue } from "../platform/feedback/feedbackPorts";
import type { MotionPreference } from "../platform/settings/settingsStore";
import { cellAt, type GameState, type Move, type Player, type Transition } from "../game";

import type { ActiveAnimation } from "./animationChannel";
import { createExtractionClone } from "./extractionClone";
import {
  BOARD_SIZE,
  CARDINAL_VECTORS,
  type ExtractionPlan,
  type Position,
} from "./extractionPlanner";
import type { ExtractionSequenceStep } from "./extractionSequence";
import { planTransitionExtraction } from "./transitionExtraction";
import { passageExitOffset } from "./transitionGeometry";
import { transitionTiming } from "./transitionTiming";

export type AnimationFeedback = Readonly<{
  emit(cue: FeedbackCue): void;
}>;

export type BoardTransitionPlayerProps = Readonly<{
  active: ActiveAnimation | null;
  boardRoot: RefObject<HTMLElement | null>;
  motion: MotionPreference;
  feedback?: AnimationFeedback;
  viewFrom: Player;
  onFinish: (id: number) => void;
}>;

export function BoardTransitionPlayer({
  active,
  boardRoot,
  motion,
  feedback,
  viewFrom,
  onFinish,
}: BoardTransitionPlayerProps) {
  useEffect(() => {
    if (active === null) return;
    const abort = new AbortController();
    const retainedVisuals = new Set<() => void>();
    const retain = (cleanup: () => void): void => {
      retainedVisuals.add(cleanup);
    };
    const cleanup = (): void => {
      for (const release of retainedVisuals) release();
      retainedVisuals.clear();
    };
    const mediaReduced = globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const reduced = motion === "reduced" || (motion === "system" && mediaReduced);

    void playBoardTransition(
      boardRoot.current,
      active,
      viewFrom,
      reduced,
      feedback,
      abort.signal,
      retain,
    ).finally(() => {
      if (!abort.signal.aborted) onFinish(active.id);
    });

    return () => {
      abort.abort();
      cleanup();
    };
  }, [active, boardRoot, feedback, motion, onFinish, viewFrom]);

  return null;
}

async function playBoardTransition(
  root: HTMLElement | null,
  active: ActiveAnimation,
  viewFrom: Player,
  reduced: boolean,
  feedback: AnimationFeedback | undefined,
  signal: AbortSignal,
  retain: (cleanup: () => void) => void,
): Promise<void> {
  if (root === null || signal.aborted) return;
  const grid = root.querySelector<HTMLElement>(".game-board__grid");
  const overlay = root.querySelector<HTMLElement>(".game-board__overlay");
  if (grid === null || overlay === null) return;

  const { request } = active;
  const reverse = request.playback?.direction === "reverse" ? request.playback : null;
  const forward = reverse?.forward ?? request;
  const sequence = planTransitionExtraction(
    forward.previousGame,
    forward.move,
    forward.transition.events,
  );
  const timing = transitionTiming(
    reduced ? "reduced" : "full",
    sequence?.steps.length ?? 0,
    forward.move.kind === "passage",
    request.timing ?? "normal",
  );

  if (reverse !== null) {
    await playReverseTransition(
      grid,
      overlay,
      forward,
      sequence?.steps ?? [],
      timing,
      viewFrom,
      reduced,
      feedback,
      signal,
      retain,
    );
    return;
  }

  feedback?.emit("press");
  await animateDisc(
    grid,
    overlay,
    forward.move.from,
    forward.move.to,
    forward.move.kind === "passage",
    viewFrom,
    timing.discMs,
    reduced,
    signal,
    retain,
  );
  if (isAborted(signal)) return;
  feedback?.emit(forward.move.kind === "passage" ? "confirm" : "contact");

  if (sequence === null) return;
  const orphanCleanup = forward.transition.events.find((event) => event.kind === "orphan-cleanup");
  for (const step of sequence.steps) {
    if (isAborted(signal)) return;
    feedback?.emit("scrape");
    const carriedStack =
      step.phase === "orphan" && orphanCleanup?.kind === "orphan-cleanup"
        ? (orphanCleanup.fields.find(({ position }) => samePosition(position, step.target))
            ?.stack ?? [])
        : null;
    await animateExtraction(
      grid,
      overlay,
      step.target,
      step.plan,
      carriedStack,
      timing.extractionStepMs,
      reduced,
      viewFrom,
      signal,
      retain,
    );
    feedback?.emit("lift");
  }
}

async function playReverseTransition(
  grid: HTMLElement,
  overlay: HTMLElement,
  forward: Readonly<{
    previousGame: GameState;
    move: Move;
    transition: Transition;
  }>,
  steps: readonly ExtractionSequenceStep[],
  timing: Readonly<{ discMs: number; extractionStepMs: number }>,
  viewFrom: Player,
  reduced: boolean,
  feedback: AnimationFeedback | undefined,
  signal: AbortSignal,
  retain: (cleanup: () => void) => void,
): Promise<void> {
  const orphanCleanup = forward.transition.events.find((event) => event.kind === "orphan-cleanup");
  for (const step of [...steps].reverse()) {
    if (isAborted(signal)) return;
    feedback?.emit("lift");
    const carriedStack =
      step.phase === "orphan" && orphanCleanup?.kind === "orphan-cleanup"
        ? (orphanCleanup.fields.find(({ position }) => samePosition(position, step.target))
            ?.stack ?? [])
        : null;
    await animateReverseExtraction(
      grid,
      overlay,
      step,
      carriedStack,
      timing.extractionStepMs,
      reduced,
      viewFrom,
      signal,
      retain,
    );
    feedback?.emit("scrape");
  }

  if (isAborted(signal)) return;
  feedback?.emit(forward.move.kind === "passage" ? "confirm" : "contact");
  await animateReverseDisc(
    grid,
    overlay,
    forward.previousGame,
    forward.move,
    timing.discMs,
    reduced,
    viewFrom,
    signal,
    retain,
  );
  if (!isAborted(signal)) feedback?.emit("press");
}

async function animateReverseDisc(
  grid: HTMLElement,
  overlay: HTMLElement,
  previousGame: GameState,
  move: Move,
  duration: number,
  reduced: boolean,
  viewFrom: Player,
  signal: AbortSignal,
  retain: (cleanup: () => void) => void,
): Promise<void> {
  const sourceCell = cellAt(previousGame, move.from);
  if (sourceCell === null || sourceCell.kind === "removed") return delay(duration, signal);
  const player = sourceCell.stack.at(-1);
  if (player === undefined) return delay(duration, signal);
  const sourceRect = discRect(grid, move.from, sourceCell.stack.length - 1, viewFrom);
  const destinationCell = move.kind === "normal" ? cellAt(previousGame, move.to) : null;
  const destinationRect =
    destinationCell !== null && destinationCell.kind !== "removed"
      ? positionRect(grid, move.to, viewFrom)
      : null;
  const sourceCenter = center(sourceRect);
  const targetCenter =
    destinationRect === null || destinationCell === null || destinationCell.kind === "removed"
      ? passageExitCenter(sourceCenter, move.from, move.to, viewFrom, inferGridStep(grid))
      : landingCenterForRect(destinationRect, destinationCell.stack.length);
  const clone = createDiscClone(grid, player, sourceCell.stack.length - 1);
  const gridRect = grid.getBoundingClientRect();
  makeOverlayClone(clone, sourceRect, gridRect, "passo-animation-clone--disc");
  overlay.append(clone);
  const cleanup = (): void => clone.remove();
  retain(cleanup);

  try {
    await runAnimation(
      clone,
      discFrames(sourceCenter, targetCenter, move.kind === "passage", reduced),
      {
        duration,
        easing: reduced ? "ease-out" : "cubic-bezier(.22,.8,.25,1)",
        fill: "forwards",
        direction: "reverse",
      },
      signal,
    );
  } catch (error) {
    cleanup();
    throw error;
  }
}

async function animateReverseExtraction(
  grid: HTMLElement,
  overlay: HTMLElement,
  step: ExtractionSequenceStep,
  carriedStack: readonly Player[] | null,
  duration: number,
  reduced: boolean,
  viewFrom: Player,
  signal: AbortSignal,
  retain: (cleanup: () => void) => void,
): Promise<void> {
  const gridRect = grid.getBoundingClientRect();
  const cellStep = inferGridStep(grid);
  if (step.plan.kind === "push") {
    const clones = step.plan.positions.flatMap((position) => {
      const isTarget = samePosition(position, step.target);
      const prepared = prepareReverseTileClone(
        grid,
        overlay,
        position,
        isTarget ? carriedStack : null,
        gridRect,
        viewFrom,
        retain,
      );
      return prepared === null ? [] : [prepared];
    });
    const vector = projectVector(CARDINAL_VECTORS[step.plan.direction], viewFrom);
    await Promise.all(
      clones.map((clone, index) =>
        runAnimation(
          clone,
          extractionFrames(vector, cellStep, reduced, index === clones.length - 1),
          {
            duration,
            easing: "cubic-bezier(.3,.72,.22,1)",
            fill: "forwards",
            direction: "reverse",
          },
          signal,
        ),
      ),
    );
    return;
  }

  const clone = prepareReverseTileClone(
    grid,
    overlay,
    step.target,
    carriedStack,
    gridRect,
    viewFrom,
    retain,
  );
  if (clone === null) return delay(duration, signal);
  const direction = step.plan.kind === "lift" ? null : step.plan.direction;
  const vector =
    direction === null
      ? { row: 0, column: 0 }
      : projectVector(CARDINAL_VECTORS[direction], viewFrom);
  const distance = step.plan.kind === "gap" ? 0.55 : 1.2;
  await runAnimation(
    clone,
    liftFrames(vector, cellStep, distance, reduced),
    {
      duration,
      easing: "cubic-bezier(.3,.68,.2,1)",
      fill: "forwards",
      direction: "reverse",
    },
    signal,
  );
}

async function animateDisc(
  grid: HTMLElement,
  overlay: HTMLElement,
  from: Position,
  to: Position,
  passage: boolean,
  viewFrom: Player,
  duration: number,
  reduced: boolean,
  signal: AbortSignal,
  retain: (cleanup: () => void) => void,
): Promise<void> {
  const sourceField = findField(grid, from);
  if (sourceField === null) return delay(duration, signal);
  const discs = sourceField.querySelectorAll<HTMLElement>(".game-board__disc");
  const sourceDisc = discs[discs.length - 1];
  if (sourceDisc === undefined) return delay(duration, signal);
  const gridRect = grid.getBoundingClientRect();
  const sourceRect = sourceDisc.getBoundingClientRect();
  const destinationField = passage ? null : findField(grid, to);
  const destinationRect = destinationField?.getBoundingClientRect();
  const cellStep = inferCellStep(grid, sourceField);
  const sourceCenter = center(sourceRect);
  const targetCenter =
    destinationRect !== undefined && destinationField !== null
      ? landingCenter(destinationField, destinationRect)
      : passageExitCenter(sourceCenter, from, to, viewFrom, cellStep);
  const clone = sourceDisc.cloneNode(true) as HTMLElement;
  makeOverlayClone(clone, sourceRect, gridRect, "passo-animation-clone--disc");
  overlay.append(clone);
  sourceDisc.style.visibility = "hidden";
  const cleanup = (): void => {
    sourceDisc.style.removeProperty("visibility");
    clone.remove();
  };
  retain(cleanup);

  try {
    await runAnimation(
      clone,
      discFrames(sourceCenter, targetCenter, passage, reduced),
      { duration, easing: reduced ? "ease-out" : "cubic-bezier(.22,.8,.25,1)", fill: "forwards" },
      signal,
    );
  } catch (error) {
    cleanup();
    throw error;
  }
}

function discFrames(
  sourceCenter: Readonly<{ x: number; y: number }>,
  targetCenter: Readonly<{ x: number; y: number }>,
  passage: boolean,
  reduced: boolean,
): Keyframe[] {
  const x = targetCenter.x - sourceCenter.x;
  const y = targetCenter.y - sourceCenter.y;
  if (reduced) {
    return [
      { opacity: 0.72, transform: "translate3d(0, 0, 0)" },
      { opacity: passage ? 0 : 1, transform: translate(x, y, 0) },
    ];
  }
  return [
    { offset: 0, transform: "translate3d(0, 0, 0)" },
    { offset: 0.22, transform: "translate3d(0, -12px, 0)" },
    { offset: 0.82, transform: translate(x, y - 7, 0), opacity: 1 },
    { offset: 1, transform: translate(x, y, 0), opacity: passage ? 0 : 1 },
  ];
}

async function animateExtraction(
  grid: HTMLElement,
  overlay: HTMLElement,
  target: Position,
  plan: ExtractionPlan,
  carriedStack: readonly Player[] | null,
  duration: number,
  reduced: boolean,
  viewFrom: Player,
  signal: AbortSignal,
  retain: (cleanup: () => void) => void,
): Promise<void> {
  const targetField = findField(grid, target);
  if (targetField === null) return delay(duration, signal);
  const gridRect = grid.getBoundingClientRect();
  const cellStep = inferCellStep(grid, targetField);

  if (plan.kind === "push") {
    const clones = plan.positions.flatMap((position) => {
      const field = findField(grid, position);
      if (field === null) return [];
      const isTarget = samePosition(position, target);
      const clone = createExtractionClone(field, carriedStack !== null || !isTarget);
      if (isTarget && carriedStack !== null) replaceCloneStack(clone, carriedStack);
      makeOverlayClone(
        clone,
        field.getBoundingClientRect(),
        gridRect,
        "passo-animation-clone--tile",
      );
      overlay.append(clone);
      field.style.visibility = "hidden";
      const cleanup = (): void => {
        field.style.removeProperty("visibility");
        clone.remove();
      };
      retain(cleanup);
      return [{ clone, cleanup }];
    });
    const vector = projectVector(CARDINAL_VECTORS[plan.direction], viewFrom);
    try {
      await Promise.all(
        clones.map(({ clone }, index) =>
          runAnimation(
            clone,
            extractionFrames(vector, cellStep, reduced, index === clones.length - 1),
            { duration, easing: "cubic-bezier(.3,.72,.22,1)", fill: "forwards" },
            signal,
          ),
        ),
      );
    } catch (error) {
      for (const { cleanup } of clones) cleanup();
      throw error;
    }
    return;
  }

  const clone = createExtractionClone(targetField, carriedStack !== null);
  if (carriedStack !== null) replaceCloneStack(clone, carriedStack);
  makeOverlayClone(
    clone,
    targetField.getBoundingClientRect(),
    gridRect,
    "passo-animation-clone--tile",
  );
  overlay.append(clone);
  targetField.style.visibility = "hidden";
  const cleanup = (): void => {
    targetField.style.removeProperty("visibility");
    clone.remove();
  };
  retain(cleanup);
  const direction = plan.kind === "lift" ? null : plan.direction;
  const vector =
    direction === null
      ? { row: 0, column: 0 }
      : projectVector(CARDINAL_VECTORS[direction], viewFrom);
  const distance = plan.kind === "gap" ? 0.55 : 1.2;
  try {
    await runAnimation(
      clone,
      liftFrames(vector, cellStep, distance, reduced),
      { duration, easing: "cubic-bezier(.3,.68,.2,1)", fill: "forwards" },
      signal,
    );
  } catch (error) {
    cleanup();
    throw error;
  }
}

function projectVector(
  vector: Readonly<{ row: number; column: number }>,
  viewFrom: Player,
): Readonly<{ row: number; column: number }> {
  return viewFrom === "black" ? vector : { row: -vector.row, column: -vector.column };
}

function extractionFrames(
  vector: Readonly<{ row: number; column: number }>,
  step: Readonly<{ x: number; y: number }>,
  reduced: boolean,
  extracted: boolean,
): Keyframe[] {
  const x = vector.column * step.x;
  const y = vector.row * step.y;
  if (reduced)
    return [{ opacity: 0.75 }, { transform: translate(x, y, 0), opacity: extracted ? 0 : 1 }];
  return [
    { offset: 0, transform: "translate3d(0, 0, 0)" },
    { offset: 0.15, transform: translate(x * -0.035, y * -0.035, 0) },
    { offset: 0.72, transform: translate(x, y, 0), opacity: 1 },
    {
      offset: 1,
      transform: `${translate(x, y - (extracted ? 18 : 0), 0)} scale(${extracted ? 0.97 : 1})`,
      opacity: extracted ? 0 : 1,
    },
  ];
}

function liftFrames(
  vector: Readonly<{ row: number; column: number }>,
  step: Readonly<{ x: number; y: number }>,
  distance: number,
  reduced: boolean,
): Keyframe[] {
  const x = vector.column * step.x * distance;
  const y = vector.row * step.y * distance;
  if (reduced) return [{ opacity: 0.8 }, { transform: translate(x, y, 0), opacity: 0 }];
  return [
    { offset: 0, transform: "translate3d(0, 0, 0) rotateX(0deg)", opacity: 1 },
    { offset: 0.58, transform: `${translate(x, y, 0)} rotateX(0deg)`, opacity: 1 },
    { offset: 1, transform: `${translate(x, y - 20, 0)} rotateX(-7deg) scale(.96)`, opacity: 0 },
  ];
}

function findField(grid: HTMLElement, position: Position): HTMLElement | null {
  const cell = grid.querySelector<HTMLElement>(
    `[data-position="${position.row}:${position.column}"]`,
  );
  return cell?.querySelector<HTMLElement>(".game-board__field") ?? null;
}

function prepareReverseTileClone(
  grid: HTMLElement,
  overlay: HTMLElement,
  position: Position,
  stack: readonly Player[] | null,
  gridRect: DOMRect,
  viewFrom: Player,
  retain: (cleanup: () => void) => void,
): HTMLElement | null {
  const field = findField(grid, position);
  const template = field ?? grid.querySelector<HTMLElement>(".game-board__field");
  const clone =
    template === null ? createTileClone() : createExtractionClone(template, stack !== null);
  clone.classList.remove(
    "game-board__field--selectable",
    "game-board__field--selected",
    "game-board__field--legal-target",
    "game-board__field--passage-target",
    "game-board__field--buried",
  );
  if (stack !== null) replaceCloneStack(clone, stack);
  else clone.querySelector(".game-board__stack")?.remove();
  clone.classList.toggle("game-board__field--full", stack?.length === 3);
  makeOverlayClone(
    clone,
    positionRect(grid, position, viewFrom),
    gridRect,
    "passo-animation-clone--tile",
  );
  overlay.append(clone);
  if (field !== null) field.style.visibility = "hidden";
  retain(() => {
    field?.style.removeProperty("visibility");
    clone.remove();
  });
  return clone;
}

function createDiscClone(grid: HTMLElement, player: Player, stackLevel: number): HTMLElement {
  const template = grid.querySelector<HTMLElement>(".game-board__disc");
  const clone =
    template === null ? document.createElement("span") : (template.cloneNode(true) as HTMLElement);
  clone.className = `game-board__disc game-board__disc--${player}`;
  if (template === null) {
    const side = document.createElement("span");
    side.className = "game-board__disc-side";
    const face = document.createElement("span");
    face.className = "game-board__disc-face";
    clone.append(side, face);
  }
  clone.style.setProperty("--stack-level", String(stackLevel));
  return clone;
}

function positionRect(grid: HTMLElement, position: Position, viewFrom: Player): DOMRect {
  const cell = grid.querySelector<HTMLElement>(
    `[data-position="${position.row}:${position.column}"]`,
  );
  const field = findField(grid, position);
  const rect = field?.getBoundingClientRect() ?? cell?.getBoundingClientRect();
  if (rect !== undefined) return rect;
  const entries = Array.from(grid.querySelectorAll<HTMLElement>("[data-position]"))
    .map((element) => {
      const field = element.querySelector<HTMLElement>(".game-board__field");
      const row = Number.parseInt(element.style.gridRow, 10) - 1;
      const column = Number.parseInt(element.style.gridColumn, 10) - 1;
      return field === null || !Number.isFinite(row) || !Number.isFinite(column)
        ? null
        : { row, column, rect: field.getBoundingClientRect() };
    })
    .filter((entry): entry is { row: number; column: number; rect: DOMRect } => entry !== null);
  const first = entries[0];
  if (first !== undefined) {
    const horizontal = entries.find(
      (entry) => entry.row === first.row && entry.column !== first.column,
    );
    const vertical = entries.find(
      (entry) => entry.column === first.column && entry.row !== first.row,
    );
    const step = inferGridStep(grid);
    const stepX =
      horizontal === undefined
        ? step.x
        : (horizontal.rect.left - first.rect.left) / (horizontal.column - first.column);
    const stepY =
      vertical === undefined
        ? step.y
        : (vertical.rect.top - first.rect.top) / (vertical.row - first.row);
    const screen = screenPosition(position, viewFrom);
    return rectAt(
      first.rect.left + (screen.column - first.column) * stepX,
      first.rect.top + (screen.row - first.row) * stepY,
      first.rect.width,
      first.rect.height,
    );
  }
  const gridRect = grid.getBoundingClientRect();
  const style = getComputedStyle(grid);
  const columnGap = Number.parseFloat(style.columnGap) || 0;
  const rowGap = Number.parseFloat(style.rowGap) || columnGap;
  const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
  const paddingRight = Number.parseFloat(style.paddingRight) || 0;
  const paddingTop = Number.parseFloat(style.paddingTop) || 0;
  const paddingBottom = Number.parseFloat(style.paddingBottom) || 0;
  const width =
    (gridRect.width - paddingLeft - paddingRight - columnGap * (BOARD_SIZE - 1)) / BOARD_SIZE;
  const height =
    (gridRect.height - paddingTop - paddingBottom - rowGap * (BOARD_SIZE - 1)) / BOARD_SIZE;
  const screen = screenPosition(position, viewFrom);
  return rectAt(
    gridRect.left + paddingLeft + screen.column * (width + columnGap),
    gridRect.top + paddingTop + screen.row * (height + rowGap),
    width,
    height,
  );
}

function createTileClone(): HTMLElement {
  const clone = document.createElement("button");
  clone.className = "game-board__field";
  clone.disabled = true;
  const face = document.createElement("span");
  face.className = "game-board__tile-face";
  clone.append(face);
  return clone;
}

function discRect(
  grid: HTMLElement,
  position: Position,
  stackLevel: number,
  viewFrom: Player,
): DOMRect {
  const fieldRect = positionRect(grid, position, viewFrom);
  const prototype = grid.querySelector<HTMLElement>(".game-board__disc")?.getBoundingClientRect();
  const width = prototype?.width ?? fieldRect.width * 0.72;
  const height = prototype?.height ?? fieldRect.height * 0.72;
  const lift = Math.max(4, fieldRect.width * 0.075) * stackLevel;
  return rectAt(
    fieldRect.left + (fieldRect.width - width) / 2,
    fieldRect.top + (fieldRect.height - height) / 2 - lift,
    width,
    height,
  );
}

function screenPosition(position: Position, viewFrom: Player): Position {
  return viewFrom === "black"
    ? position
    : { row: BOARD_SIZE - 1 - position.row, column: BOARD_SIZE - 1 - position.column };
}

function rectAt(left: number, top: number, width: number, height: number): DOMRect {
  return {
    x: left,
    y: top,
    left,
    top,
    width,
    height,
    right: left + width,
    bottom: top + height,
    toJSON: () => ({}),
  };
}

function inferGridStep(grid: HTMLElement): { x: number; y: number } {
  const field = grid.querySelector<HTMLElement>(".game-board__field");
  if (field === null) return { x: 0, y: 0 };
  return inferCellStep(grid, field);
}

function samePosition(left: Position, right: Position): boolean {
  return left.row === right.row && left.column === right.column;
}

/** The previous-game board does not yet contain a disc that landed on an orphaned destination. */
function replaceCloneStack(clone: HTMLElement, stack: readonly Player[]): void {
  let stackElement = clone.querySelector<HTMLElement>(".game-board__stack");
  if (stackElement === null) {
    stackElement = document.createElement("span");
    stackElement.className = "game-board__stack";
    stackElement.setAttribute("aria-hidden", "true");
    clone.append(stackElement);
  }
  stackElement.replaceChildren(
    ...stack.map((player, index) => {
      const disc = document.createElement("span");
      disc.className = `game-board__disc game-board__disc--${player}`;
      disc.style.setProperty("--stack-level", String(index));
      return disc;
    }),
  );
}

function inferCellStep(grid: HTMLElement, field: HTMLElement): { x: number; y: number } {
  const gridStyle = getComputedStyle(grid);
  const gap = Number.parseFloat(gridStyle.columnGap) || 0;
  const rect = field.getBoundingClientRect();
  return { x: rect.width + gap, y: rect.height + gap };
}

function makeOverlayClone(
  clone: HTMLElement,
  rect: DOMRect,
  gridRect: DOMRect,
  extraClass: string,
): void {
  clone.setAttribute("aria-hidden", "true");
  clone.removeAttribute("id");
  clone.removeAttribute("tabindex");
  clone.classList.add("passo-animation-clone", extraClass);
  Object.assign(clone.style, {
    position: "absolute",
    left: `${rect.left - gridRect.left}px`,
    top: `${rect.top - gridRect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    margin: "0",
    right: "auto",
    bottom: "auto",
    pointerEvents: "none",
  });
}

function center(rect: DOMRect): { x: number; y: number } {
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

function landingCenter(field: HTMLElement, rect: DOMRect): { x: number; y: number } {
  return landingCenterForRect(rect, field.querySelectorAll(".game-board__disc").length);
}

function landingCenterForRect(rect: DOMRect, existingHeight: number): { x: number; y: number } {
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2 - existingHeight * Math.max(4, rect.width * 0.075),
  };
}

function passageExitCenter(
  source: Readonly<{ x: number; y: number }>,
  from: Position,
  to: Position,
  viewFrom: Player,
  step: Readonly<{ x: number; y: number }>,
): { x: number; y: number } {
  const offset = passageExitOffset(from, to, viewFrom, step);
  return {
    x: source.x + offset.x,
    y: source.y + offset.y,
  };
}

function translate(x: number, y: number, z: number): string {
  return `translate3d(${x}px, ${y}px, ${z}px)`;
}

async function runAnimation(
  element: HTMLElement,
  frames: Keyframe[],
  options: KeyframeAnimationOptions,
  signal: AbortSignal,
): Promise<void> {
  if (signal.aborted) return;
  if (typeof element.animate !== "function") {
    await runTimerAnimationFallback(element, frames, options, signal);
    return;
  }
  const animation = element.animate(frames, options);
  const cancel = (): void => animation.cancel();
  signal.addEventListener("abort", cancel, { once: true });
  try {
    await animation.finished.catch(() => undefined);
  } finally {
    signal.removeEventListener("abort", cancel);
  }
}

type TweenTransform = Readonly<{
  x: number;
  y: number;
  z: number;
  rotateX: number;
  scale: number;
}>;

type TweenFrame = Readonly<{
  offset: number;
  opacity: number;
  transform: TweenTransform;
}>;

async function runTimerAnimationFallback(
  element: HTMLElement,
  frames: Keyframe[],
  options: KeyframeAnimationOptions,
  signal: AbortSignal,
): Promise<void> {
  const duration = Number(options.duration) || 0;
  const orderedFrames = materializedFrames(frames, options.direction === "reverse");
  const first = orderedFrames[0];
  if (first === undefined || duration <= 0) return;

  applyTweenFrame(element, first);
  const startedAt = Date.now();
  while (!signal.aborted) {
    const elapsed = Date.now() - startedAt;
    const progress = Math.min(1, elapsed / duration);
    applyTweenProgress(element, orderedFrames, progress, options.easing ?? "linear");
    if (progress >= 1) return;
    await delay(Math.min(16, duration - elapsed), signal);
  }
}

function materializedFrames(frames: Keyframe[], reverse: boolean): readonly TweenFrame[] {
  const lastIndex = Math.max(1, frames.length - 1);
  let priorTransform = parseTweenTransform();
  let priorOpacity = 1;
  const normalized = frames.map((frame, index): TweenFrame => {
    if (typeof frame["transform"] === "string") {
      priorTransform = parseTweenTransform(frame["transform"]);
    }
    if (typeof frame["opacity"] === "number") priorOpacity = frame["opacity"];
    return {
      offset: typeof frame.offset === "number" ? frame.offset : index / lastIndex,
      opacity: priorOpacity,
      transform: priorTransform,
    };
  });
  if (!reverse) return normalized;
  return normalized.reverse().map((frame) => ({ ...frame, offset: 1 - frame.offset }));
}

function applyTweenProgress(
  element: HTMLElement,
  frames: readonly TweenFrame[],
  progress: number,
  easing: string,
): void {
  const endIndex = frames.findIndex((frame) => frame.offset >= progress);
  const end = frames[endIndex < 0 ? frames.length - 1 : endIndex];
  const start = frames[Math.max(0, (endIndex < 0 ? frames.length - 1 : endIndex) - 1)];
  if (start === undefined || end === undefined) return;
  const span = Math.max(Number.EPSILON, end.offset - start.offset);
  const segmentProgress = fallbackEase(
    Math.min(1, Math.max(0, (progress - start.offset) / span)),
    easing,
  );
  applyTweenFrame(element, {
    offset: progress,
    opacity: interpolate(start.opacity, end.opacity, segmentProgress),
    transform: {
      x: interpolate(start.transform.x, end.transform.x, segmentProgress),
      y: interpolate(start.transform.y, end.transform.y, segmentProgress),
      z: interpolate(start.transform.z, end.transform.z, segmentProgress),
      rotateX: interpolate(start.transform.rotateX, end.transform.rotateX, segmentProgress),
      scale: interpolate(start.transform.scale, end.transform.scale, segmentProgress),
    },
  });
}

function applyTweenFrame(element: HTMLElement, frame: TweenFrame): void {
  const { x, y, z, rotateX, scale } = frame.transform;
  element.style.transform = `${translate(x, y, z)} rotateX(${rotateX}deg) scale(${scale})`;
  element.style.opacity = String(frame.opacity);
}

function parseTweenTransform(value = ""): TweenTransform {
  const translateMatch =
    /translate3d\(\s*(-?[\d.]+)(?:px)?\s*,\s*(-?[\d.]+)(?:px)?\s*,\s*(-?[\d.]+)(?:px)?\s*\)/.exec(
      value,
    );
  const rotateMatch = /rotateX\(\s*(-?[\d.]+)deg\s*\)/.exec(value);
  const scaleMatch = /scale\(\s*(-?(?:\d+(?:\.\d+)?|\.\d+))\s*\)/.exec(value);
  return {
    x: Number(translateMatch?.[1] ?? 0),
    y: Number(translateMatch?.[2] ?? 0),
    z: Number(translateMatch?.[3] ?? 0),
    rotateX: Number(rotateMatch?.[1] ?? 0),
    scale: Number(scaleMatch?.[1] ?? 1),
  };
}

function fallbackEase(progress: number, easing: string): number {
  if (easing === "linear") return progress;
  return 1 - (1 - progress) ** 3;
}

function interpolate(start: number, end: number, progress: number): number {
  return start + (end - start) * progress;
}

function delay(duration: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted || duration <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = globalThis.setTimeout(resolve, duration);
    signal.addEventListener(
      "abort",
      () => {
        globalThis.clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}

function isAborted(signal: AbortSignal): boolean {
  return signal.aborted;
}
